import React, { useState, useCallback, useMemo, useEffect } from "react";

import MuiCheckbox from "@material-ui/core/Checkbox";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import ListSubheader from "@material-ui/core/ListSubheader";
import { makeStyles, Theme } from "@material-ui/core/styles";
import ExpandMore from "@material-ui/icons/ExpandMore";
import MUIAutocomplete, {
  AutocompleteRenderGroupParams,
  AutocompleteRenderInputParams,
} from "@material-ui/lab/Autocomplete";
import partition from "lodash/partition";

import { colorsDark, colorsLight } from "../../themes";
import { TextField } from "../TextField";
import { Caption } from "../Typography";
import { DropdownOption, useStylesListSubheader } from "./shared";

type IdType<T extends DropdownOption> = T extends { id: infer Id } ? Id : never;

export interface SearchableMultiSelectDropdownProps<T extends DropdownOption> {
  /** Array of options to display */
  options: T[];
  /** Array of selected option IDs */
  selectedValues: Array<IdType<T>>;
  /** Callback when selection changes */
  onChange: (selectedIds: Array<IdType<T>>) => void;
  /** Label for the dropdown */
  label?: React.ReactNode;
  /** Placeholder text when no selection */
  placeholder?: string;
  /** Helper text below the input */
  helperText?: React.ReactNode;
  /** Custom render function for options in the list */
  renderOption?: (option: T) => React.ReactNode;
  /** Custom function to get the label text from an option */
  getOptionLabel?: (option: T) => string;
  /** Disabled state */
  disabled?: boolean;
  /** Error state */
  error?: boolean;
  /** Maximum height of the dropdown menu */
  maxMenuHeight?: number | string;
  /** Controlled search value (for backend search); pair with onSearchChange */
  searchValue?: string;
  /** Callback when the search input text changes (for backend search) */
  onSearchChange?: (value: string) => void;
  /** Test ID for testing */
  "data-testid"?: string;
}

// For Storybook documentation
export function SearchableMultiSelectDropdownForProps<T extends DropdownOption>(
  props: SearchableMultiSelectDropdownProps<T>
) {}

const SELECTED_GROUP = "Selected";
const UNSELECTED_GROUP = "Unselected";

const useStyles = makeStyles((theme: Theme) => {
  const isDarkTheme = theme.palette.type === "dark";
  const colors = isDarkTheme ? colorsDark : colorsLight;

  return {
    root: {
      "& .MuiAutocomplete-inputRoot": {
        paddingRight: `${theme.spacing(4)}px !important`,
      },
    },
    paper: {
      backgroundColor: isDarkTheme ? colors.background.flyout : colors.background.card,
      marginTop: 1,
      boxShadow: "0px 0px 1px rgba(0, 0, 0, 0.12), 0px 2px 1px rgba(0, 0, 0, 0.24)",
    },
    listbox: {
      padding: 0,
      maxHeight: ({ maxMenuHeight }: { maxMenuHeight?: number | string }) => maxMenuHeight || 300,
    },
    option: {
      borderTop: `1px solid ${colors.gray.divider}`,
      padding: theme.spacing(1.5, 1.5),
      "&:first-child": {
        borderTop: "none",
      },
      '&[aria-selected="true"]': {
        backgroundColor: "transparent",
      },
      '&[data-focus="true"]': {
        backgroundColor: colors.gray.light,
      },
    },
    groupLabel: {
      backgroundColor: colors.background.input,
      borderTop: `1px solid ${colors.gray.divider}`,
      padding: theme.spacing(1, 1.5),
      lineHeight: "normal",
      "&:first-of-type": {
        borderTop: "none",
      },
    },
    groupUl: {
      padding: 0,
    },
    endAdornment: {
      position: "absolute",
      right: theme.spacing(1.5),
      top: "50%",
      transform: "translateY(-50%)",
    },
    chevronButton: {
      width: 40,
      height: 40,
      padding: 0,
      marginRight: -theme.spacing(1),
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    popupIndicator: {
      color: colors.font.tertiary,
      transition: "transform 200ms",
    },
    popupIndicatorOpen: {
      transform: "rotate(180deg)",
    },
    checkbox: {
      marginRight: theme.spacing(1),
      padding: 0,
    },
    optionContent: {
      display: "flex",
      alignItems: "center",
      width: "100%",
    },
    optionLabel: {
      flex: 1,
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
  };
});

export default function SearchableMultiSelectDropdown<T extends DropdownOption>({
  options,
  selectedValues,
  onChange,
  label,
  placeholder,
  helperText,
  renderOption: renderOptionOverride,
  getOptionLabel: getOptionLabelOverride,
  disabled = false,
  error = false,
  maxMenuHeight,
  searchValue: externalSearchValue,
  onSearchChange,
  "data-testid": dataTestId,
}: SearchableMultiSelectDropdownProps<T>): React.ReactElement {
  const [internalInputValue, setInternalInputValue] = useState("");
  const isControlledSearch = externalSearchValue !== undefined;
  const inputValue = isControlledSearch ? externalSearchValue : internalInputValue;
  const [isOpen, setIsOpen] = useState(false);

  const classes = useStyles({ maxMenuHeight });
  const listSubheaderClasses = useStylesListSubheader();

  // Get label for an option
  const getOptionLabel = useCallback(
    (option: T): string => {
      if (getOptionLabelOverride) {
        return getOptionLabelOverride(option);
      }
      if (typeof option.label === "string") {
        return option.label;
      }
      return option.id;
    },
    [getOptionLabelOverride]
  );

  // Check if an option is selected
  const isOptionSelected = useCallback(
    (option: T): boolean => {
      return selectedValues.includes(option.id as IdType<T>);
    },
    [selectedValues]
  );

  // Group options into Selected and Unselected
  const groupedOptions = useMemo(() => {
    const [selected, unselected] = partition(options, isOptionSelected);
    return [
      ...selected.map((opt) => ({ ...opt, _group: SELECTED_GROUP })),
      ...unselected.map((opt) => ({ ...opt, _group: UNSELECTED_GROUP })),
    ] as (T & { _group: string })[];
  }, [options, isOptionSelected]);

  // Filter options based on input value
  const filterOptions = useCallback(
    (opts: (T & { _group: string })[], state: { inputValue: string }) => {
      const query = state.inputValue.toLowerCase().trim();
      if (!query) {
        return opts;
      }
      return opts.filter((option) => {
        const labelText = getOptionLabel(option as T).toLowerCase();
        return labelText.includes(query);
      });
    },
    [getOptionLabel]
  );

  // Toggle selection for an option
  const toggleOption = useCallback(
    (option: T) => {
      const optionId = option.id as IdType<T>;
      if (selectedValues.includes(optionId)) {
        onChange(selectedValues.filter((id) => id !== optionId));
      } else {
        onChange([...selectedValues, optionId]);
      }
    },
    [selectedValues, onChange]
  );

  // Get display value for the input
  const getInputDisplayValue = useCallback((): string => {
    if (selectedValues.length === 0) {
      return "";
    }
    if (selectedValues.length === 1) {
      const selectedOption = options.find((opt) => opt.id === selectedValues[0]);
      if (selectedOption) {
        return getOptionLabel(selectedOption);
      }
      return String(selectedValues[0]);
    }
    return `${selectedValues.length} selected`;
  }, [selectedValues, options, getOptionLabel]);

  // Render the option content
  // Note: MUI Autocomplete already provides role="option" and aria-selected on the parent <li>
  const renderOptionContent = useCallback(
    (option: T & { _group: string }) => {
      const isSelected = isOptionSelected(option);
      return (
        <div className={classes.optionContent} onClick={() => toggleOption(option)}>
          <MuiCheckbox checked={isSelected} color="primary" className={classes.checkbox} disabled={option.disabled} />
          <span className={classes.optionLabel}>
            {renderOptionOverride ? renderOptionOverride(option) : getOptionLabel(option)}
          </span>
        </div>
      );
    },
    [
      classes.optionContent,
      classes.checkbox,
      classes.optionLabel,
      isOptionSelected,
      toggleOption,
      renderOptionOverride,
      getOptionLabel,
    ]
  );

  // Render group header
  const renderGroup = useCallback(
    (params: AutocompleteRenderGroupParams) => {
      const { group, children } = params;
      const count = group === SELECTED_GROUP ? selectedValues.length : options.length - selectedValues.length;

      // Don't render empty groups
      if (count === 0) {
        return null;
      }

      return (
        <li key={group}>
          <ListSubheader classes={listSubheaderClasses} component="div" disableSticky>
            <Caption color="textPrimary">
              {group} ({count})
            </Caption>
          </ListSubheader>
          <ul className={classes.groupUl}>{children}</ul>
        </li>
      );
    },
    [selectedValues.length, options.length, listSubheaderClasses, classes.groupUl]
  );

  // Handle chevron button click to toggle dropdown
  const handleChevronClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (!disabled) {
        setIsOpen((prev) => !prev);
      }
    },
    [disabled]
  );

  // Render input field
  const renderInput = useCallback(
    (params: AutocompleteRenderInputParams) => {
      const displayValue = isOpen ? inputValue : getInputDisplayValue();

      return (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          error={error}
          disabled={disabled}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <InputAdornment position="end" className={classes.endAdornment}>
                <IconButton
                  className={classes.chevronButton}
                  onClick={handleChevronClick}
                  disabled={disabled}
                  aria-label={isOpen ? "Close dropdown" : "Open dropdown"}
                  tabIndex={-1}
                >
                  <ExpandMore className={`${classes.popupIndicator} ${isOpen ? classes.popupIndicatorOpen : ""}`} />
                </IconButton>
              </InputAdornment>
            ),
          }}
          inputProps={{
            ...params.inputProps,
            value: displayValue,
          }}
          data-testid={dataTestId}
        />
      );
    },
    [
      isOpen,
      inputValue,
      getInputDisplayValue,
      label,
      placeholder,
      helperText,
      error,
      disabled,
      classes.endAdornment,
      classes.chevronButton,
      classes.popupIndicator,
      classes.popupIndicatorOpen,
      dataTestId,
      handleChevronClick,
    ]
  );

  // Clear input value when dropdown closes
  useEffect(() => {
    if (!isOpen) {
      if (!isControlledSearch) {
        setInternalInputValue("");
      }
      onSearchChange?.("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <MUIAutocomplete
      multiple
      disableCloseOnSelect
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      options={groupedOptions}
      value={groupedOptions.filter((opt) => isOptionSelected(opt))}
      onChange={() => {}} // Selection handled via option click
      inputValue={inputValue}
      onInputChange={(_event, newValue, reason) => {
        if (reason === "input") {
          if (!isControlledSearch) {
            setInternalInputValue(newValue);
          }
          onSearchChange?.(newValue);
        }
      }}
      filterOptions={filterOptions}
      groupBy={(option) => option._group}
      getOptionLabel={(option) => getOptionLabel(option)}
      getOptionSelected={(option, value) => option.id === value.id}
      getOptionDisabled={(option) => option.disabled || false}
      renderInput={renderInput}
      renderOption={renderOptionContent}
      renderGroup={renderGroup}
      renderTags={() => null}
      disableClearable
      forcePopupIcon={false}
      disabled={disabled}
      classes={{
        root: classes.root,
        paper: classes.paper,
        listbox: classes.listbox,
        option: classes.option,
        groupLabel: classes.groupLabel,
        groupUl: classes.groupUl,
      }}
    />
  );
}
